import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Plus } from "lucide-react";

interface ActionButtonConfig {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  showCreateButton?: boolean;
  createButtonLabel?: string;
  onCreateClick?: () => void;
  className?: string;
  actions?: React.ReactNode;
  actionButtons?: ActionButtonConfig[];
  /** Optional filter/toolbar row rendered in a divided strip beneath the title. */
  toolbar?: React.ReactNode;
}

/*
 * huntvox page-header: a full-bleed bordered band above the page body —
 * serif display title, muted description, actions pinned right, with an
 * optional toolbar strip divided off underneath.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  showCreateButton = false,
  createButtonLabel = "Create",
  onCreateClick,
  className,
  actions,
  actionButtons = [],
  toolbar,
}) => {
  return (
    <div className={cn("border-b bg-background", className)}>
      <div className="flex flex-col gap-3 px-4 py-3 sm:px-6 md:flex-row md:items-start md:justify-between xl:gap-4 xl:py-4">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold tracking-tight sm:text-2xl xl:text-3xl">
            {title}
          </h1>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {actions}

          {actionButtons.map((button, index) => (
            <Button
              key={`action-btn-${index}`}
              onClick={button.onClick}
              variant={button.variant || "default"}
              className={button.className || ""}
            >
              {button.icon}
              {button.label}
            </Button>
          ))}

          {showCreateButton && (
            <Button onClick={onCreateClick}>
              <Plus className="size-4" />
              {createButtonLabel}
            </Button>
          )}
        </div>
      </div>

      {toolbar && (
        <div className="border-t px-4 pb-3 sm:px-6 xl:pb-4">{toolbar}</div>
      )}
    </div>
  );
};

export default PageHeader;
